import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react'

export type SectionVariant = 'default' | 'cancel-only' | 'select'

export interface SectionDef {
  name: string
  title: string | ReactNode
  /** In sequential mode, allow finishing the wizard after this step */
  canFinishAfter?: boolean
  /**
   * Action button layout for this step.
   * - `default`: standard navigation ("Zurück" / "Weiter" / "Speichern")
   * - `cancel-only`: only render "Abbrechen" — used when the step advances itself
   *   on user interaction (e.g. selecting a row in a search step)
   * - `select`: like `default`, but the "Weiter" button advances and is gated on
   *   the step's own selection (`canProceed`) instead of full-form validity — used
   *   by search/selection steps where the entity must be chosen before proceeding
   */
  variant?: SectionVariant
  /** Override the terminal button label when this section is the last step. Defaults to `Speichern`. */
  finishLabel?: string | ReactNode
  /** Override the terminal button icon when this section is the last step. Defaults to the save icon. */
  finishIcon?: ReactNode
  /**
   * Section that only exists while creating (e.g. an entity-search/selection step).
   * Shown as a wizard step in `sequential` mode, omitted from the `tabbed` edit rail.
   */
  createOnly?: boolean
}

/**
 * `sequential` — create wizard (steps rail, one step at a time).
 * `tabbed` — edit a multi-section record: all non-`createOnly` sections as a vertical tab rail.
 * `focused` — edit a single section (no rail): one section with save/cancel actions.
 */
export type SectionFlowMode = 'sequential' | 'tabbed' | 'focused'

export interface UseSectionFlowOptions {
  sections: readonly SectionDef[]
  /** Default: 'sequential' (or 'focused' if `section` is set without `mode` — backward compat). */
  mode?: SectionFlowMode
  /** Required when mode === 'focused'. In `tabbed` mode: the initially active tab (optional). */
  section?: string
}

export type SectionNames<T extends readonly SectionDef[]> = T[number]['name']

export type SectionNamesExcept<T extends readonly SectionDef[], E extends SectionNames<T>> = Exclude<SectionNames<T>, E>

export interface SectionFlowReturn {
  mode: SectionFlowMode
  sections: readonly SectionDef[]
  /** Sequential: current step. Focused: the focused section. Tabbed: current tab. */
  active: string
  currentIndex: number
  visited: Set<string>
  /** Section names rendered as tabs in `tabbed` mode (non-`createOnly`). */
  included: Set<string>
  canGoNext: boolean
  canGoPrev: boolean
  canFinishNow: boolean
  isLastStep: boolean
  goToNext: () => void
  goToPrev: () => void
  goTo: (sectionName: string) => void
}

function resolveMode(modeIn: SectionFlowMode | undefined, section: string | undefined): SectionFlowMode {
  if (modeIn) return modeIn
  if (section != null) return 'focused'
  return 'sequential'
}

export const useSectionFlow = ({ sections, mode: modeIn, section }: UseSectionFlowOptions): SectionFlowReturn => {
  const mode = resolveMode(modeIn, section)

  if (mode === 'focused' && section == null) {
    throw new Error("useSectionFlow: mode='focused' requires a `section` name")
  }

  const included = useMemo<Set<string>>(() => {
    if (mode === 'tabbed') return new Set(sections.filter(s => !s.createOnly).map(s => s.name))
    return new Set(sections.map(s => s.name))
  }, [mode, sections])

  const [current, setCurrent] = useState(0)

  const firstIncludedIndex = useMemo(() => {
    const index = sections.findIndex(s => included.has(s.name))
    return index === -1 ? 0 : index
  }, [sections, included])

  const initialTabIndex = useMemo(() => {
    if (mode === 'tabbed' && section != null) {
      const index = sections.findIndex(s => s.name === section)
      if (index !== -1 && included.has(section)) return index
    }
    return firstIncludedIndex
  }, [mode, section, sections, included, firstIncludedIndex])

  const [tabCurrent, setTabCurrent] = useState(initialTabIndex)

  useEffect(() => {
    if (mode !== 'tabbed') return
    const activeName = sections[tabCurrent]?.name
    if (activeName != null && included.has(activeName)) return
    setTabCurrent(firstIncludedIndex)
  }, [mode, sections, tabCurrent, included, firstIncludedIndex])

  const activeIndex =
    mode === 'focused' ? sections.findIndex(s => s.name === section) : mode === 'tabbed' ? tabCurrent : current

  const active = sections[activeIndex]?.name ?? sections[0]?.name ?? ''

  const [visited, setVisited] = useState<Set<string>>(() => new Set([active]))

  useEffect(() => {
    setVisited(prev => {
      if (prev.has(active)) return prev
      return new Set(prev).add(active)
    })
  }, [active])

  const isLastStep = activeIndex === sections.length - 1
  const isFirstStep = activeIndex === 0

  const goToNext = useCallback(() => {
    if (mode !== 'sequential') return
    setCurrent(prev => Math.min(prev + 1, sections.length - 1))
  }, [mode, sections.length])

  const goToPrev = useCallback(() => {
    if (mode !== 'sequential') return
    setCurrent(prev => Math.max(prev - 1, 0))
  }, [mode])

  const goTo = useCallback(
    (sectionName: string) => {
      const targetIndex = sections.findIndex(s => s.name === sectionName)
      if (targetIndex === -1) return

      if (mode === 'sequential') {
        setCurrent(targetIndex)
        return
      }

      if (mode === 'tabbed' && included.has(sectionName)) {
        setTabCurrent(targetIndex)
      }
    },
    [mode, sections, included],
  )

  return {
    mode,
    active,
    sections,
    currentIndex: activeIndex,
    visited,
    included,
    canGoNext: mode === 'sequential' && !isLastStep,
    canGoPrev: mode === 'sequential' && !isFirstStep,
    canFinishNow: mode === 'sequential' && (isLastStep || (sections[activeIndex]?.canFinishAfter ?? false)),
    isLastStep,
    goToNext,
    goToPrev,
    goTo,
  }
}
